import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function Services() {
  const { t } = useTranslation();

  const engAnim = useScrollAnimation();
  const addAnim = useScrollAnimation();
  const ctaAnim = useScrollAnimation();

  const engineeringServices = [
    {
      key: 'mechanical',
      icon: 'ri-settings-3-line',
      image: 'https://readdy.ai/api/search-image?query=Precision%20mechanical%20lathe%20workshop%20with%20diverse%20team%20including%20black%20African%20and%20white%20European%20technicians%20working%20together%20on%20metal%20parts%20and%20machinery%2C%20steel%20blue%20lighting%20accents%2C%20warm%20copper%20undertones%2C%20clean%20organized%20industrial%20workspace%2C%20professional%20engineering%20photography&width=600&height=400&seq=service-mechanical&orientation=landscape',
    },
    {
      key: 'electricity',
      icon: 'ri-flashlight-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20electrical%20installation%20work%20with%20diverse%20team%20of%20electricians%20including%20mixed-race%20and%20albino%20technicians%20working%20with%20cables%20and%20panels%2C%20modern%20electrical%20room%20with%20steel%20blue%20and%20warm%20copper%20lighting%20accents%2C%20clean%20professional%20workspace&width=600&height=400&seq=service-electricity&orientation=landscape',
    },
    {
      key: 'hydraulic',
      icon: 'ri-water-flash-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20water%20treatment%20system%20and%20hydraulic%20pumps%20with%20diverse%20engineering%20team%2C%20pipes%20and%20valves%20with%20steel%20blue%20ambient%20lighting%2C%20clean%20industrial%20facility%20with%20warm%20copper%20highlights%2C%20professional%20photography&width=600&height=400&seq=service-hydraulic&orientation=landscape',
    },
    {
      key: 'electromechanics',
      icon: 'ri-building-2-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20elevator%20and%20escalator%20maintenance%20with%20diverse%20team%20of%20technicians%20including%20black%20African%20and%20white%20European%20professionals%2C%20clean%20building%20interior%20with%20steel%20blue%20and%20warm%20copper%20ambient%20lighting%2C%20professional%20engineering%20photography&width=600&height=400&seq=service-electromechanics&orientation=landscape',
    },
    {
      key: 'hvac',
      icon: 'ri-windy-line',
      image: 'https://readdy.ai/api/search-image?query=Industrial%20HVAC%20air%20conditioning%20system%20installation%20with%20diverse%20team%20working%20on%20ventilation%20ducts%20and%20machinery%2C%20clean%20modern%20industrial%20setting%20with%20steel%20blue%20and%20copper%20lighting%2C%20professional%20engineering%20photography&width=600&height=400&seq=service-hvac&orientation=landscape',
    },
    {
      key: 'electronics',
      icon: 'ri-cpu-line',
      image: 'https://readdy.ai/api/search-image?query=Electronic%20security%20system%20installation%20with%20CCTV%20cameras%20and%20control%20panels%2C%20diverse%20team%20of%20technicians%20including%20mixed-race%20and%20albino%20professionals%2C%20modern%20technology%20setup%20with%20steel%20blue%20and%20warm%20copper%20lighting%2C%20clean%20professional%20workspace&width=600&height=400&seq=service-electronics&orientation=landscape',
    },
  ];

  const additionalServices = [
    { key: 'tickets', icon: 'ri-plane-line' },
    { key: 'tourism', icon: 'ri-map-pin-2-line' },
    { key: 'office', icon: 'ri-stack-line' },
    { key: 'visas', icon: 'ri-passport-line' },
    { key: 'rentacar', icon: 'ri-car-line' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[360px] md:h-[480px] flex items-center justify-center bg-primary-950">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20industrial%20engineering%20services%20composition%20with%20gears%20and%20tools%2C%20dark%20charcoal%20background%20with%20steel%20blue%20and%20copper%20accents%2C%20modern%20minimalist%20design%2C%20clean%20precise%20composition%20inspired%20by%20African%20diversity&width=1600&height=900&seq=services-hero-deiaspart&orientation=landscape"
            alt="DEIASPART Services"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-background-50 mb-3">
            {t('services.hero.title')}
          </h1>
          <p className="text-background-300 text-base md:text-lg max-w-xl mx-auto">
            {t('services.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Engineering Services */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={engAnim.ref}
            className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
              engAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="text-accent-500 font-medium text-sm tracking-wider uppercase mb-3 block">
              {t('services.engineering.desc')}
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
              {t('services.engineering.title')}
            </h2>
          </div>

          <div className="space-y-12 md:space-y-16 max-w-6xl mx-auto">
            {engineeringServices.map((service, i) => (
              <div
                key={service.key}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center transition-all duration-700 ${
                  engAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className={`${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="rounded-2xl overflow-hidden aspect-[3/2]">
                    <img
                      src={service.image}
                      alt={t(`service.${service.key}.name`)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className={`${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-primary-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950 mb-3">
                    {t(`service.${service.key}.name`)}
                  </h3>
                  <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                    {t(`services.detail.${service.key}`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={addAnim.ref}
            className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
              addAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="text-accent-500 font-medium text-sm tracking-wider uppercase mb-3 block">
              {t('services.additional.desc')}
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
              {t('services.additional.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
            {additionalServices.map((service, i) => (
              <div
                key={service.key}
                className={`bg-background-50 rounded-xl p-6 border border-background-200/70 hover:border-accent-300 transition-all duration-300 hover:-translate-y-1 ${
                  addAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-accent-50 flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-accent-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-2 text-base">
                  {t(`service.${service.key}.name`)}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  {t(`services.detail.${service.key}`)}
                </p>
                <a
                  href={`mailto:geral@deiaspart.co.ao?subject=${encodeURIComponent(t('contact.services.emailSubject') + t(`service.${service.key}.name`))}`}
                  className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-600 text-sm font-medium transition-colors"
                >
                  {t('services.card.request')}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary-950">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={ctaAnim.ref}
            className={`text-center max-w-2xl mx-auto transition-all duration-700 ${
              ctaAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-background-50 mb-4">
              {t('services.cta.text')}
            </h2>
            <a
              href="mailto:geral@deiaspart.co.ao"
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-8 py-4 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
            >
              {t('services.cta.button')}
              <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}